import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, FileBarChart, FileText, Library, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";
import { computeSynopsis } from "@/lib/calc";
import { compact } from "@/lib/format";
import { blankClient } from "@/lib/migrate";

export default function ClientsPage() {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const nav = useNavigate();
  const load = () => api.list().then(setClients).catch(() => toast.error("Could not load clients"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const c = await api.create(blankClient(`Client ${(clients?.length || 0) + 1}`));
    nav(`/clients/${c.id}`);
  };
  const duplicate = async (id) => { await api.duplicate(id); toast.success("Client duplicated"); load(); };
  const remove = async () => { await api.remove(toDelete.id); toast.success("Client deleted"); setToDelete(null); load(); };
  const list = (clients || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen">
      <AppHeader>
        <Button variant="outline" onClick={() => nav("/products")} data-testid="product-library-button" className="gap-2 hidden lg:inline-flex"><Library className="h-4 w-4" /> Product library</Button>
        <Button onClick={create} data-testid="add-client-button" className="gap-1.5" size="sm"><Plus className="h-4 w-4" /> <span className="hidden sm:inline">New client</span><span className="sm:hidden">New</span></Button>
      </AppHeader>
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5 sm:mb-8 fade-up">
          <div>
            <p className="metric-label">Advisor workspace</p>
            <h1 className="font-heading text-2xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Financial Needs Analysis</h1>
            <p className="text-sm text-slate-500 mt-2">{clients ? `${clients.length} client records` : "Loading…"}. Open a client to enter data, every figure recalculates instantly.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="client-search-input" placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
          </div>
        </div>
        <div className="client-table fade-up" style={{ animationDelay: "80ms" }} data-testid="client-table">
          <div className="client-table-head">
            <span>Client</span><span>Life insurance needed</span><span>Critical illness gap</span><span>Retirement / month</span><span className="text-right">Actions</span>
          </div>
          {list.map((c) => {
            const s = computeSynopsis(c.data);
            const cur = s.d.profile.currency;
            return (
              <div key={c.id} className="client-table-row" data-testid={`client-row-${c.id}`}>
                <Link to={`/clients/${c.id}`} className="min-w-0" data-testid={`client-open-${c.id}`}>
                  <span className="block font-semibold text-slate-900 truncate hover:text-blue-700">{c.name}</span>
                  <span className="block text-xs text-slate-500">{s.age ? `Age ${s.age}` : ""}{s.d.profile.spouse.name ? ` · Spouse: ${s.d.profile.spouse.name}` : ""}</span>
                </Link>
                <span data-label="Life insurance needed" className={`font-mono text-sm ${s.ins.client.lifeAdditional > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`client-life-${c.id}`}>{s.ins.client.lifeAdditional > 0 ? compact(s.ins.client.lifeAdditional, cur) : "Covered"}</span>
                <span data-label="Critical illness gap" className={`font-mono text-sm ${s.ins.client.ciAdditional > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`client-ci-${c.id}`}>{s.ins.client.ciAdditional > 0 ? compact(s.ins.client.ciAdditional, cur) : "Covered"}</span>
                <span data-label="Retirement / month" className={`font-mono text-sm ${s.ret.monthlySurplus < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`client-ret-${c.id}`}>{s.ret.monthlySurplus < 0 ? "Shortfall " : "Surplus "}{compact(Math.abs(s.ret.monthlySurplus), cur)}</span>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => nav(`/clients/${c.id}/fna`)} data-testid={`client-fna-${c.id}`} aria-label="FNA Report" title="FNA Report"><FileBarChart className="h-4 w-4 text-[#0072C6]" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => nav(`/clients/${c.id}/report`)} data-testid={`client-report-${c.id}`} aria-label="Detailed synopsis" title="Detailed synopsis"><FileText className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => duplicate(c.id)} data-testid={`client-duplicate-${c.id}`} aria-label="Duplicate"><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setToDelete(c)} data-testid={`client-delete-${c.id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
            );
          })}
          {clients && list.length === 0 && (
            <div className="p-10 text-center" data-testid="clients-empty">
              <p className="text-sm text-slate-500 mb-4">No clients yet. Each new client starts empty with your formulas and standard assumptions ready.</p>
              <Button onClick={create} data-testid="empty-add-client-button" className="gap-2"><Plus className="h-4 w-4" /> Create first client</Button>
            </div>
          )}
        </div>
      </main>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the client record and its synopsis.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-button" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
